import { computed, makeAutoObservable, runInAction } from 'mobx';
import {
  ContestInformation,
  ContestSpotlight,
  ContestStage,
  ContestWithUserAccount,
  PaginateLeaderboardOptions,
} from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';

export class ContestStore {
  private contests = new Map<string, unknown>();
  private isFetchingSpotlight = false;
  private contestInformationPromise: Promise<ContestInformation> | null = null;

  spotlight: ContestSpotlight = {
    userContests: [],
    all: {
      [ContestStage.Active]: [],
      [ContestStage.Upcoming]: [],
      [ContestStage.Closed]: [],
    },
  };

  contestInformation: ContestInformation = {
    userContests: [],
    nextContestId: null,
    topPerformers: [],
    all: {
      [ContestStage.Active]: [],
      [ContestStage.Upcoming]: [],
      [ContestStage.Closed]: [],
    },
  };

  constructor() {
    makeAutoObservable(this);
  }

  getContestWithAccount(contestId: string, influencerCode?: string) {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return contestService
      .getContestWithAccount(contestId, influencerCode)
      .then((contest) => {
        this.contests.set(contestId, contest);
        return contest;
      });
  }

  async getSpotlight() {
    // Prevent multiple simultaneous calls
    if (this.isFetchingSpotlight) {
      return this.spotlight;
    }

    this.isFetchingSpotlight = true;
    try {
      const contestInformation = await this.getContestInformation();

      // Extract spotlight data from contest information
      const spotlight: ContestSpotlight = {
        userContests: contestInformation.userContests,
        all: contestInformation.all,
      };

      runInAction(() => {
        this.spotlight = spotlight;
      });

      return spotlight;
    } finally {
      runInAction(() => {
        this.isFetchingSpotlight = false;
      });
    }
  }

  async getContestToRegister() {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return await contestService.getContestToRegister();
  }

  async getContestInformation() {
    if (this.contestInformationPromise) {
      return this.contestInformationPromise;
    }

    this.contestInformationPromise = this.fetchContestInformation();
    return this.contestInformationPromise;
  }

  private async fetchContestInformation(): Promise<ContestInformation> {
    try {
      const {
        apiFactory: { contestService },
      } = getEnv();

      const contestInformation = await contestService.getContestInformation();
      runInAction(() => {
        this.contestInformation = contestInformation;
        this.contestInformationPromise = null;
      });
      return contestInformation;
    } catch (error) {
      runInAction(() => {
        this.contestInformationPromise = null;
      });
      throw error;
    }
  }

  async getLeaderboard(
    contestId: string,
    options?: PaginateLeaderboardOptions,
  ) {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return await contestService.getLeaderboard(contestId, options);
  }

  async getOldLeaderboard(
    contestId: string,
    options?: PaginateLeaderboardOptions,
  ) {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return await contestService.getOldLeaderboard(contestId, options);
  }

  async getPinnedLeaderboardAccounts(contestId: string) {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return await contestService.getPinnedLeaderboardAccounts(contestId);
  }

  async getCurrentUserLeaderboardAccount(contestId: string) {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return await contestService.getCurrentUserLeaderboardAccount(contestId);
  }

  registerToContest(contestId: string, influencerCode?: string) {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return contestService.registerToContest(contestId, influencerCode);
  }

  pinUserToContest(contestId: string, publicId: string) {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return contestService.pinUser(contestId, publicId);
  }

  unPinUserToContest(contestId: string, publicId: string) {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return contestService.unpinUser(contestId, publicId);
  }

  async validateInfluencerCode(code: string): Promise<ContestWithUserAccount> {
    const {
      apiFactory: { contestService },
    } = getEnv();

    return await contestService.validateInfluencerCode(code);
  }

  setContestInformation(contestInformation: ContestInformation) {
    this.contestInformation = contestInformation;
  }

  @computed get hasActiveContest(): boolean {
    return this.spotlight.userContests.some(
      (contest) => contest.stage !== ContestStage.Closed,
    );
  }
}
